'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import { Bus, ChevronLeft, Filter, Users, Star, MapPin, Wifi, Wind, Tv, Bath, Coffee, CarFront, SortDesc, Calendar } from 'lucide-react';
import Header from '../components/Header';

function AmenityIcon({ name }) {
    switch (name) {
        case 'wifi': return <Wifi className="h-4 w-4" />;
        case 'ac': return <Wind className="h-4 w-4" />;
        case 'entertainment': return <Tv className="h-4 w-4" />;
        case 'restroom': return <Bath className="h-4 w-4" />;
        case 'refreshments': return <Coffee className="h-4 w-4" />;
        case 'luxury': return <CarFront className="h-4 w-4" />;
        default: return null;
    }
}

function AvailabilityBadge({ availability }) {
    const colors = {
        available: 'bg-green-100 text-green-800',
        limited: 'bg-yellow-100 text-yellow-800',
        booked: 'bg-red-100 text-red-800',
    };
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[availability] || colors.available}`}>
            {availability}
        </span>
    );
}

export default function SearchResults() {
    const router = useRouter();
    const { vehicleType, startDate, returnDate, passengers } = router.query;

    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('rating');
    const [filters, setFilters] = useState({ wifi: false, ac: false, entertainment: false, restroom: false, refreshments: false, luxury: false });
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        if (!router.isReady) return;
        const fetchVehicles = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (vehicleType && vehicleType !== 'all') params.append('vehicleType', vehicleType);
                if (startDate) params.append('startDate', startDate);
                if (returnDate) params.append('returnDate', returnDate);
                if (passengers) params.append('capacity', passengers); // Map passengers to capacity filter

                const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/buses?${params.toString()}`);
                let data = await res.json();
                if (!Array.isArray(data)) data = [];

                // Client-side amenity filtering (since the API already does it, but we can keep it as backup or use it to refine)
                const activeAmenities = Object.entries(filters).filter(([, v]) => v).map(([k]) => k);
                if (activeAmenities.length > 0) {
                    data = data.filter(v => activeAmenities.every(a => v.amenities && v.amenities[a]));
                }

                // Sort (API also does some sorting but results might need refinement)
                if (sortBy === 'price-day-asc') data.sort((a, b) => a.pricePerDay - b.pricePerDay);
                else if (sortBy === 'price-day-desc') data.sort((a, b) => b.pricePerDay - a.pricePerDay);
                else if (sortBy === 'capacity') data.sort((a, b) => b.capacity - a.capacity);

                setVehicles(data);
            } catch (e) {
                console.error(e);
                setVehicles([]);
            } finally {
                setLoading(false);
            }
        };
        fetchVehicles();
    }, [router.isReady, vehicleType, startDate, returnDate, passengers, sortBy, filters]);

    return (
        <div className="min-h-screen bg-gray-50">
            <Head><title>Available {vehicleType || 'Vehicles'} - BlisssBus</title></Head>

            {/* Header */}
            <Header />

            <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                {/* Back + Title */}
                {/* Back + Title */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
                    <div className="flex items-center gap-3">
                        <button onClick={() => router.back()} className="flex items-center justify-center h-10 w-10 bg-white border border-gray-200 rounded-xl text-gray-600 hover:text-green-600 hover:border-green-100 transition-all shadow-sm">
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <div className="flex flex-col">
                            <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 capitalize tracking-tight">{vehicleType || 'All'} Vehicles</h1>
                            {(startDate || returnDate) && (
                                <div className="flex items-center gap-2 mt-1 text-xs text-green-600 font-bold bg-green-50 px-3 py-1 rounded-full border border-green-100 w-fit">
                                    <Calendar className="h-3 w-3" />
                                    <span>{startDate || '?'} to {returnDate || '?'}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest sm:ml-auto">
                        {vehicles.length} options available
                    </span>
                </div>

                {/* Controls */}
                {/* Controls */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <div className="flex items-center gap-2 grow">
                        <div className="bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex items-center gap-2 grow sm:grow-0 shadow-sm">
                            <SortDesc className="h-4 w-4 text-gray-400" />
                            <select
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value)}
                                className="bg-transparent border-none text-xs font-bold text-gray-700 focus:ring-0 outline-none grow"
                            >
                                <option value="rating">Top Rated</option>
                                <option value="price-day-asc">Lowest Price</option>
                                <option value="price-day-desc">Highest Price</option>
                                <option value="capacity">Capacity</option>
                            </select>
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center justify-center gap-2 border rounded-xl px-4 py-2.5 text-xs font-bold transition-all grow sm:grow-0 shadow-sm ${
                                showFilters ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 text-gray-700'
                            }`}
                        >
                            <Filter className="h-4 w-4" />
                            Filters {Object.values(filters).some(v => v === true) && '•'}
                        </button>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                        <h3 className="font-semibold mb-4">Filter by Amenities</h3>
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(filters).map(([key, val]) => (
                                <button
                                    key={key}
                                    onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${val ? 'bg-green-600 text-white border-green-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <AmenityIcon name={key} />
                                    <span className="capitalize">{key}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="flex justify-center items-center py-24">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                    </div>
                )}

                {/* Results Grid */}
                {!loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {vehicles.map(vehicle => (
                            <div key={vehicle._id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col">
                                <img
                                    src={vehicle.imageUrl || `https://source.unsplash.com/800x400/?${vehicle.type},bus,travel`}
                                    alt={vehicle.name}
                                    className="w-full h-48 object-cover"
                                    onError={e => { e.target.src = 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&h=400&fit=crop'; }}
                                />
                                <div className="flex-1 p-5 lg:p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1 mr-2">
                                            <h2 className="font-extrabold text-gray-900 text-lg leading-tight group-hover:text-green-600 transition-colors">
                                                {vehicle.comfortLevel || vehicle.name}
                                            </h2>
                                            {vehicle.location && (
                                                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 mt-1 uppercase tracking-wider">
                                                    <MapPin className="h-3 w-3 text-green-500" />
                                                    <span>{vehicle.location}</span>
                                                </div>
                                            )}
                                        </div>
                                        <AvailabilityBadge availability={vehicle.availability} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-5 border-y border-gray-50 py-4">
                                        <div className="flex items-center gap-2.5">
                                            <div className="bg-gray-50 p-2 rounded-lg">
                                                <Users className="h-4 w-4 text-gray-400" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Seating</p>
                                                <p className="text-xs font-bold text-gray-700">{vehicle.capacity} People</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2.5">
                                            <div className="bg-green-50 p-2 rounded-lg">
                                                <CarFront className="h-4 w-4 text-green-600" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Base Rate</p>
                                                <p className="text-xs font-bold text-gray-700">₹{vehicle.pricePerDay}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Amenities */}
                                    <div className="flex flex-wrap gap-2 mb-5">
                                        {vehicle.amenities && Object.entries(vehicle.amenities)
                                            .filter(([, v]) => v)
                                            .slice(0, 3)
                                            .map(([key]) => (
                                                <span key={key} className="flex items-center gap-1.5 bg-gray-50 text-gray-500 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border border-gray-100">
                                                    <AmenityIcon name={key} />
                                                    <span>{key}</span>
                                                </span>
                                            ))}
                                    </div>

                                    {/* Rating */}
                                    <div className="flex items-center justify-between mt-auto">
                                        <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-l-2 border-green-500 pl-3">
                                            <span>{vehicle.rating}</span>
                                            <div className="flex items-center gap-0.5">
                                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                            </div>
                                            <span className="text-[10px] text-gray-400 font-medium">({vehicle.reviews})</span>
                                        </div>
                                        <div className="text-right">
                                            {vehicle.pricePerKm && (
                                                <p className="text-[10px] font-bold text-green-600 uppercase tracking-tight">+₹{vehicle.pricePerKm}/km only</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 bg-gray-50/50">
                                    {vehicle.availability === 'booked' ? (
                                        <button
                                            disabled
                                            className="block w-full text-center bg-gray-200 text-gray-400 font-bold py-3 rounded-xl cursor-not-allowed text-xs transition-all uppercase tracking-widest"
                                        >
                                            Unavailable
                                        </button>
                                    ) : (
                                        <Link
                                            href={`/book/${vehicle._id}`}
                                            className="block w-full text-center bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl transition-all text-xs shadow-lg shadow-green-600/20 uppercase tracking-widest"
                                        >
                                            Book Now
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && vehicles.length === 0 && (
                    <div className="text-center py-24">
                        <Bus className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700">No vehicles found</h3>
                        <p className="text-gray-500 mt-2">Try adjusting your filters or search a different vehicle type.</p>
                        <Link href="/" className="mt-6 inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors">
                            Back to Home
                        </Link>
                    </div>
                )}
            </main>
        </div>
    );
}
